/**
 * PcoDrawer — the PCO Log's right-hand detail panel. Clicking a log row slides
 * this in with everything the slim table dropped: MOD/FWA references, the full
 * money picture, comments, and the submission ledger (submissions expand to
 * their member weeks in place). The log stays visible and scannable next to
 * it; the full command-center page is one click deeper ("Open PCO →").
 *
 * Data comes entirely from what the page already holds: the SettingsPco row
 * and the project-wide submissions prefetch. The drawer fetches nothing.
 */
import { useEffect, useState } from 'react';
import { formatDateShort, formatMoneyCents, formatPcoNumber, formatSubmissionLabel, formatWeekRangeSpan } from '../lib/format';
import { derivePcoStatus, derivedPillKind } from '../lib/pcoStatus';
import { rollupPillKind, rollupStatusLabel } from '../lib/rollupStatus';
import { ArrowRight, X } from 'lucide-react';
import { ExpandChevron, StatusPill, type StatusPillKind } from './design';
import type {
  SettingsPco,
  SubmissionStatus,
  SubmissionSummary,
} from '@coffman-tm/shared';

export function PcoDrawer({
  pco,
  submissions,
  loading,
  onClose,
  onOpenPco,
  onOpenRollup,
}: {
  pco: SettingsPco;
  /** From the page's project-wide prefetch; null while it's still loading. */
  submissions: SubmissionSummary[] | null;
  loading: boolean;
  onClose: () => void;
  onOpenPco: () => void;
  onOpenRollup: (weekEndingDate: string) => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const hasRefs = pco.mod_numbers.length > 0 || pco.hoffman_fwas.length > 0;

  const burnPct =
    pco.approved_total_cents > 0
      ? Math.round((pco.billed_to_date_cents / pco.approved_total_cents) * 100)
      : null;

  const ordered =
    submissions === null
      ? null
      : [...submissions].sort((a, b) =>
          a.submission_number.localeCompare(b.submission_number, undefined, { numeric: true }),
        );

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} aria-hidden />
      <aside
        className="pco-drawer"
        role="dialog"
        aria-label={`PCO ${formatPcoNumber(pco.pco_number)} detail`}
      >
        <div className="pco-drawer-head">
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.09em',
                textTransform: 'uppercase',
                color: 'var(--ink-4)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              PCO {formatPcoNumber(pco.pco_number)}
              {(() => {
                // Whole PCO in — the pill reads funding_pending_cents, so a
                // week awaiting the GC's approval can't demote it.
                const { kind, label } = derivePcoStatus(pco);
                return <StatusPill kind={derivedPillKind(kind)}>{label}</StatusPill>;
              })()}
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.35, marginTop: 4 }}>
              {pco.description}
            </div>
          </div>
          <button
            type="button"
            className="pdf-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={14} strokeWidth={2} aria-hidden />
          </button>
        </div>

        <div className="pco-drawer-body">
          {hasRefs && (
            <div className="drawer-refs">
              {pco.mod_numbers.length > 0 && (
                <>
                  <span className="drawer-refs-label">MOD</span>
                  <span className="mono drawer-refs-val">{pco.mod_numbers.join(', ')}</span>
                </>
              )}
              {pco.hoffman_fwas.length > 0 && (
                <>
                  <span className="drawer-refs-label">FWA</span>
                  <span className="mono drawer-refs-val">{pco.hoffman_fwas.join(' · ')}</span>
                </>
              )}
            </div>
          )}

          <div className="drawer-statgrid">
            {pco.amount_proposed_cents != null && (
              <DrawerStat label="Proposed" value={formatMoneyCents(pco.amount_proposed_cents)} />
            )}
            {pco.approved_total_cents > 0 && (
              // "Approved funding" (not bare "Approved") — money Hoffman has
              // executed a MOD/FWA for, distinct from the "Submitted to date"
              // stat below. See PcoDetailPage's stat band for the vocabulary.
              <DrawerStat label="Approved funding" value={formatMoneyCents(pco.approved_total_cents)} />
            )}
            <DrawerStat
              label="Submitted to date"
              value={pco.billed_to_date_cents !== 0 ? formatMoneyCents(pco.billed_to_date_cents) : '—'}
            />
            {pco.remaining_cents !== 0 && (
              <DrawerStat label="Remaining" value={formatMoneyCents(pco.remaining_cents)} />
            )}
            {burnPct != null && <DrawerStat label="Burn" value={`${burnPct}%`} />}
          </div>

          {pco.comments && pco.comments.trim() !== '' && (
            <p className="muted" style={{ fontSize: 12.5, margin: 0, lineHeight: 1.5 }}>
              {pco.comments}
            </p>
          )}

          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.09em',
                textTransform: 'uppercase',
                color: 'var(--ink-4)',
                marginBottom: 6,
              }}
            >
              Submissions{ordered ? ` · ${ordered.length}` : ''}
            </div>
            {ordered === null && loading ? (
              <p className="muted" style={{ fontSize: 13, margin: 0 }}>
                Loading…
              </p>
            ) : !ordered || ordered.length === 0 ? (
              <p className="muted" style={{ fontSize: 13, margin: 0 }}>
                No billing activity yet.
              </p>
            ) : (
              <div>
                {ordered.map((s) => (
                  <DrawerSubmission
                    key={s.id}
                    parentPcoNumber={pco.pco_number}
                    submission={s}
                    onOpenRollup={onOpenRollup}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="pco-drawer-actions">
          <button type="button" className="btn btn-dark" onClick={onOpenPco}>
            Open PCO {formatPcoNumber(pco.pco_number)} <ArrowRight size={14} aria-hidden />
          </button>
        </div>
      </aside>
    </>
  );
}

function DrawerStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div
        style={{
          fontSize: 9.5,
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--ink-4)',
        }}
      >
        {label}
      </div>
      <div className="mono" style={{ fontSize: 13, marginTop: 2 }}>
        {value}
      </div>
    </div>
  );
}

/** One submission line in the drawer's ledger; expands in place to its weeks. */
function DrawerSubmission({
  parentPcoNumber,
  submission,
  onOpenRollup,
}: {
  parentPcoNumber: string;
  submission: SubmissionSummary;
  onOpenRollup: (weekEndingDate: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const expandable = submission.rollups.length > 0;
  const total =
    submission.total_cents !== 0
      ? submission.total_cents
      : submission.imported_total_cents ?? 0;
  // Full worked span (first week's Sunday → last week's Saturday), matching
  // SubmissionSection's range label so the two surfaces agree.
  const range = submission.week_range
    ? formatWeekRangeSpan(submission.week_range.first_week, submission.week_range.last_week)
    : (submission.imported_range_label ?? 'No weeks yet');

  return (
    <div style={{ borderBottom: '1px solid var(--rule)' }}>
      <div
        role={expandable ? 'button' : undefined}
        tabIndex={expandable ? 0 : undefined}
        aria-expanded={expandable ? expanded : undefined}
        onClick={expandable ? () => setExpanded((v) => !v) : undefined}
        onKeyDown={
          expandable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setExpanded((v) => !v);
                }
              }
            : undefined
        }
        className="drawer-ledger-row"
        style={{
          padding: '7px 0',
          cursor: expandable ? 'pointer' : undefined,
        }}
      >
        {expandable ? (
          <ExpandChevron expanded={expanded} />
        ) : (
          <span aria-hidden />
        )}
        <span className="mono" style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)' }}>
          {formatSubmissionLabel(parentPcoNumber, submission.submission_number)}
        </span>
        <span className="muted" style={{ minWidth: 0, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {range}
        </span>
        <span className="num" style={{ fontSize: 12 }}>
          {total !== 0 ? formatMoneyCents(total) : ''}
        </span>
        <span>
          <StatusPill kind={submissionPill(submission.status)}>
            {submissionStatusLabel(submission.status)}
          </StatusPill>
        </span>
      </div>
      {expanded &&
        [...submission.rollups]
          .sort((a, b) => a.week_ending_date.localeCompare(b.week_ending_date))
          .map((r) => (
            <div
              key={r.id}
              role="button"
              tabIndex={0}
              onClick={() => onOpenRollup(r.week_ending_date)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onOpenRollup(r.week_ending_date);
              }}
              className="drawer-ledger-row"
              style={{
                padding: '5px 0',
                cursor: 'pointer',
                fontSize: 12,
              }}
              title="Open week detail"
            >
              <span aria-hidden />
              <span aria-hidden />
              <span className="muted" style={{ paddingLeft: 6 }}>
                Week ending {formatDateShort(r.week_ending_date)}
              </span>
              <span className="num">{formatMoneyCents(r.totals.invoice_total_cents)}</span>
              <span>
                <StatusPill kind={rollupPill(r.status)}>{rollupStatusLabel(r.status)}</StatusPill>
              </span>
            </div>
          ))}
    </div>
  );
}

/* Small local label helpers — same duplication-over-coupling call the log page
 * and SubmissionSection already make. (PCO pill now derives from money via
 * ../lib/pcoStatus — no stored-status pill helper here anymore.) */
function submissionPill(status: SubmissionStatus): StatusPillKind {
  // Closed is orange, not grey, per the client's money language: orange means
  // we asked, green means they said yes. A closed package is sent and waiting
  // on Hoffman — the grey 'pending' kind read as inert, which is how a sent
  // $871k week got read as unsent on 2026-07-29.
  return status === 'open' ? 'progress' : status === 'approved' ? 'ready' : 'warning';
}
function submissionStatusLabel(status: SubmissionStatus): string {
  // Matches SubmissionSection: a closed package has been fully sent to
  // Hoffman, so the pill reads "Sent".
  return status === 'open' ? 'Open' : status === 'approved' ? 'Approved' : 'Sent';
}
const rollupPill = rollupPillKind;
